import { useEffect, useRef } from 'react';

interface LazySectionProps {
  index: number;
  shouldLoad: boolean;
  onLoad: (index: number) => void;
  setRef: (el: HTMLDivElement | null) => void;
  scrollRoot: HTMLElement | null;
  children: React.ReactNode;
}

/**
 * A2F-647: wraps a viewer section block with an IntersectionObserver
 * that fires onLoad(index) once when the section enters the viewport
 * (with a 1500px pre-fetch buffer — large enough to cover roughly one
 * full PDF section ahead/behind so normal-speed scrolling never sees
 * the "Loading..." placeholder). Disconnects after the first
 * intersection so it never re-fires.
 *
 * frontend variant: observes against `scrollRoot` (the contentRef
 * container that owns the scrollbar). On first render scrollRoot may
 * be null; the observer falls back to the viewport in that case and
 * is recreated when scrollRoot becomes non-null.
 */
export function LazySection({
  index,
  shouldLoad,
  onLoad,
  setRef,
  scrollRoot,
  children,
}: LazySectionProps) {
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!shouldLoad) return;
    const el = innerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onLoad(index);
            observer.disconnect();
            break;
          }
        }
      },
      { root: scrollRoot, rootMargin: '1500px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index, shouldLoad, onLoad, scrollRoot]);

  return (
    <div
      ref={(el) => {
        innerRef.current = el;
        setRef(el);
      }}
    >
      {children}
    </div>
  );
}
